"use client";

import { emitUsageEvent } from "../lib/umami";
import { useMemo, useState, useSyncExternalStore } from "react";
import { buildBlipSnippet, type BlipSnippetLanguage } from "../lib/blip-snippet";
import type { GameReferenceData, ReferenceImage } from "./GameReferenceCatalog";
import styles from "./ReferenceExplorers.module.css";

interface Sprite {
  id: number;
  name: string;
  image?: ReferenceImage;
}
interface Colour {
  id: number;
  label: string;
  hex: string;
  variant: boolean;
}

const PAGE_SIZE = 36;
const STORAGE_KEY = "fivemdocs:blip-favorites:v1";
const FAVORITES_EVENT = "fivemdocs:blip-favorites-changed";
let memoryFavorites = "[]";

function readFavorites(): string {
  if (typeof window === "undefined") return "[]";
  try { return window.localStorage.getItem(STORAGE_KEY) ?? memoryFavorites; }
  catch { return memoryFavorites; }
}
function subscribeFavorites(notify: () => void): () => void {
  window.addEventListener(FAVORITES_EVENT, notify);
  window.addEventListener("storage", notify);
  return () => {
    window.removeEventListener(FAVORITES_EVENT, notify);
    window.removeEventListener("storage", notify);
  };
}
function writeFavorites(ids: number[]): void {
  memoryFavorites = JSON.stringify(ids);
  try { window.localStorage.setItem(STORAGE_KEY, memoryFavorites); } catch { /* optional */ }
  window.dispatchEvent(new Event(FAVORITES_EVENT));
}


export default function BlipExplorer({ data }: { data: GameReferenceData }) {
  const sprites = useMemo<Sprite[]>(() => (data.sections.find((section) => section.id === "blips")?.rows ?? [])
    .filter((row) => /^\d+$/.test(row.cells[1]?.text ?? ""))
    .map((row) => ({
      id: Number(row.cells[1].text),
      name: row.cells[2]?.text || "Unknown sprite",
      image: row.cells[0]?.images?.[0],
    })), [data]);
  const colours = useMemo<Colour[]>(() => (data.sections.find((section) => section.id === "blip-colors")?.rows ?? [])
    .map((row) => ({
      id: Number.parseInt(row.cells[1]?.text ?? "", 10),
      label: row.cells[2]?.text ?? "",
      hex: row.cells[0]?.color ?? "",
      variant: !/^\d+$/.test(row.cells[1]?.text ?? ""),
    })).filter((entry) => Number.isFinite(entry.id)), [data]);
  const standardColours = useMemo(() => colours.filter((entry) => !entry.variant), [colours]);

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<"id" | "name" | "recent">("id");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState(61);
  const [selectedColour, setSelectedColour] = useState(2);
  const [name, setName] = useState("Hospital");
  const [coords, setCoords] = useState({ x: "307.7", y: "-595.2", z: "43.3" });
  const [scale, setScale] = useState(0.8);
  const [shortRange, setShortRange] = useState(true);
  const [route, setRoute] = useState(false);
  const [language, setLanguage] = useState<BlipSnippetLanguage>("lua");
  const [copyStatus, setCopyStatus] = useState("");
  const favoritesString = useSyncExternalStore(subscribeFavorites, readFavorites, () => "[]");
  const favorites = useMemo<number[]>(() => {
    try {
      const value: unknown = JSON.parse(favoritesString);
      return Array.isArray(value) ? value.filter((id): id is number => Number.isInteger(id)) : [];
    } catch { return []; }
  }, [favoritesString]);

  const selectedSprite = sprites.find((sprite) => sprite.id === selectedId) ?? sprites[0];
  const selectedSwatch = standardColours.find((colour) => colour.id === selectedColour);
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase().replace(/[\s_-]/g, "");
    return sprites.filter((sprite) => {
      if (onlyFavorites && !favorites.includes(sprite.id)) return false;
      if (!term) return true;
      return sprite.name.toLowerCase().replace(/[\s_-]/g, "").includes(term) || String(sprite.id).includes(term);
    }).sort((a, b) => sort === "name"
      ? a.name.localeCompare(b.name)
      : sort === "recent" ? b.id - a.id : a.id - b.id);
  }, [sprites, query, onlyFavorites, favorites, sort]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const code = useMemo(() => {
    if (!selectedSprite || Object.values(coords).some((value) => value.trim() === "" || !Number.isFinite(Number(value)))) {
      return "";
    }
    try {
      return buildBlipSnippet({
        sprite: selectedSprite.id,
        colour: selectedColour,
        name,
        x: Number(coords.x),
        y: Number(coords.y),
        z: Number(coords.z),
        scale,
        shortRange,
        route,
      }, language);
    } catch { return ""; }
  }, [selectedSprite, selectedColour, name, coords, scale, shortRange, route, language]);

  async function copy(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      emitUsageEvent("example_copy");
      setCopyStatus("Copied " + label);
    } catch {
      setCopyStatus("Clipboard unavailable; select the displayed value instead.");
    }
  }
  function toggleFavorite(id: number) {
    writeFavorites(favorites.includes(id)
      ? favorites.filter((value) => value !== id) : [...favorites, id]);
  }

  return <section className={styles.explorer} aria-label="FiveM blip explorer">
    <div className={styles.heading}>
      <div>
        <h2>Explore all FiveM blip sprites</h2>
        <p>Find an icon visually, select its numeric ID, choose a colour and copy a working client-side snippet.</p>
      </div>
      <a href={data.downloadUrl} download>Download source JSON ↗</a>
    </div>
    <div className={styles.filters}>
      <label className={styles.search}>
        <span>Search blips</span>
        <input type="search" placeholder="Hospital, police, radar_hospital, 61…"
          value={query} onChange={(event) => { setQuery(event.target.value); setPage(0); }} />
      </label>
      <label>
        <span>Sort</span>
        <select value={sort} onChange={(event) => { setSort(event.target.value as typeof sort); setPage(0); }}>
          <option value="id">Sprite ID (ascending)</option>
          <option value="recent">Sprite ID (descending)</option>
          <option value="name">Name (A–Z)</option>
        </select>
      </label>
      <label>
        <span>View</span>
        <select value={view} onChange={(event) => setView(event.target.value as typeof view)}>
          <option value="grid">Visual grid</option>
          <option value="list">Compact list</option>
        </select>
      </label>
    </div>
    <div className={styles.filterRow}>
      <button type="button" aria-pressed={onlyFavorites} className={styles.chip}
        onClick={() => { setOnlyFavorites(!onlyFavorites); setPage(0); }}>
        {onlyFavorites ? "★ " : "☆ "}Favorites ({favorites.length})
      </button>
      {(query || onlyFavorites) && <button type="button" className={styles.chip}
        onClick={() => { setQuery(""); setOnlyFavorites(false); setPage(0); }}>Clear filters</button>}
      <span className={styles.muted} aria-live="polite">{filtered.length} of {sprites.length} sprites · {colours.length} source colour entries</span>
    </div>

    {selectedSprite && <div className={styles.builder} id="blip-builder">
      <div className={styles.selectedBlip}>
        <div className={styles.eyebrow}>Selected sprite · ID {selectedSprite.id}</div>
        <div className={styles.bigPreview}>
          {selectedSprite.image ? <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={selectedSprite.image.src} alt={"Preview of " + selectedSprite.name}
              loading="eager" decoding="async" />
          </> : <span>No preview available</span>}
        </div>
        <strong className={styles.spriteName}>{selectedSprite.name}</strong>
        <div className={styles.actions}>
          <button type="button" onClick={() => copy(String(selectedSprite.id), "sprite ID")}>Copy ID</button>
          <button type="button" onClick={() => toggleFavorite(selectedSprite.id)}>
            {favorites.includes(selectedSprite.id) ? "★ Saved" : "☆ Save"}
          </button>
        </div>
        <p className={styles.muted}>Sprite images are unmodified source previews. The selected colour
          is shown as a swatch, not a promise of exact in-game tinting.</p>
        <div className={styles.selectedColour}>
          <span className={styles.swatch} style={{ backgroundColor: selectedSwatch?.hex ?? "#fff" }} />
          Colour {selectedColour}: {selectedSwatch?.label ?? "Unknown"}
        </div>
      </div>
      <div className={styles.builderControls}>
        <h3>Blip code generator</h3>
        <p className={styles.muted}>client.lua or client.js · works without a framework or external dependencies.</p>
        <div className={styles.fieldGrid}>
          <label className={styles.wideField}>Blip label
            <input type="text" maxLength={80} value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label>Colour ID
            <select value={selectedColour} onChange={(event) => setSelectedColour(Number(event.target.value))}>
              {standardColours.map((colour) => <option value={colour.id} key={colour.id}>
                {colour.id} — {colour.label}
              </option>)}
            </select>
          </label>
          <label>Scale: {scale.toFixed(1)}
            <input type="range" min="0.1" max="2" step="0.1" value={scale}
              onChange={(event) => setScale(Number(event.target.value))} />
          </label>
          {(["x", "y", "z"] as const).map((axis) => <label key={axis}>
            {axis.toUpperCase()} coordinate
            <input type="number" step="any" value={coords[axis]} onChange={(event) => {
              setCoords((previous) => ({ ...previous, [axis]: event.target.value }));
            }} />
          </label>)}
        </div>
        <div className={styles.checks}>
          <label><input type="checkbox" checked={shortRange} onChange={(event) => setShortRange(event.target.checked)} />
            Show only nearby (short range)</label>
          <label><input type="checkbox" checked={route} onChange={(event) => setRoute(event.target.checked)} />
            Show GPS route</label>
        </div>
        <div className={styles.actions}>
          <div className={styles.language} role="group" aria-label="Snippet language">
            <button type="button" aria-pressed={language === "lua"} onClick={() => setLanguage("lua")}>Lua</button>
            <button type="button" aria-pressed={language === "javascript"} onClick={() => setLanguage("javascript")}>JavaScript</button>
          </div>
          <button type="button" disabled={!code} onClick={() => copy(code, "blip snippet")}>Copy code</button>
        </div>
        {code ? <pre className={styles.code}><code>{code}</code></pre>
          : <p role="alert">Enter valid, finite X/Y/Z coordinates to generate code.</p>}
        <p className={styles.muted}>Place the code in a client script declared in your fxmanifest.lua.
          Replace the example coordinates; the generated code cleans up its own blip on resource stop.</p>
      </div>
    </div>}

    <h3>All sprite IDs</h3>
    <div className={view === "grid" ? styles.blipGrid : styles.blipList} aria-label="Blip sprite results">
      {visible.map((sprite) => <div className={styles.blipCard} data-selected={selectedSprite?.id === sprite.id} key={sprite.id}>
        <button type="button" className={styles.spriteChoice}
          aria-pressed={selectedSprite?.id === sprite.id}
          aria-label={"Select " + sprite.name + ", sprite " + sprite.id}
          onClick={() => {
            setSelectedId(sprite.id);
            setName(sprite.name.replace(/^radar_/, "").replace(/_/g, " ")
              .replace(/\b\w/g, (letter) => letter.toUpperCase()));
          }}>
          <span className={styles.spriteThumb}>
            {sprite.image ? <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={sprite.image.src} alt="" loading="lazy" decoding="async" />
            </> : <span>No image</span>}
          </span>
          <span className={styles.spriteText}><strong>{sprite.id}</strong> {sprite.name}</span>
        </button>
        <button type="button" className={styles.favorite}
          aria-label={(favorites.includes(sprite.id) ? "Remove " : "Add ") + sprite.name + (favorites.includes(sprite.id) ? " from favorites" : " to favorites")}
          onClick={() => toggleFavorite(sprite.id)}>{favorites.includes(sprite.id) ? "★" : "☆"}</button>
      </div>)}
    </div>
    {visible.length === 0 && <p className={styles.empty}>No sprites match these filters. Try another name, ID, or clear Favorites.</p>}
    {pageCount > 1 && <nav className={styles.pager} aria-label="Blip sprite pages">
      <button type="button" disabled={currentPage === 0} onClick={() => setPage(0)}>First</button>
      <button type="button" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous</button>
      <span>Page {currentPage + 1} of {pageCount}</span>
      <button type="button" disabled={currentPage >= pageCount - 1} onClick={() => setPage(currentPage + 1)}>Next</button>
      <button type="button" disabled={currentPage >= pageCount - 1} onClick={() => setPage(pageCount - 1)}>Last</button>
    </nav>}
    <details className={styles.colourDetails}>
      <summary>Browse all {colours.length} documented colour entries (including friendly/enemy variants)</summary>
      <p className={styles.muted}>Variant colours share a numeric ID. The official reference uses
        <code> SET_BLIP_AS_FRIENDLY</code> for friendly/enemy state; they are not separate IDs for <code>SetBlipColour</code>.</p>
      <div className={styles.colourGrid}>
        {colours.map((colour, index) => <div className={styles.colourRow} key={index}>
          <span className={styles.swatch} style={{ backgroundColor: colour.hex || "transparent" }} />
          <span>{colour.id}{colour.variant ? " (variant)" : ""} · {colour.label}</span>
        </div>)}
      </div>
    </details>
    <p className={styles.muted} role="status">{copyStatus}</p>
    <p className={styles.sourceNote}>Source: <a href={data.sourceUrl}>official Cfx.re blips</a>,
      game build 3258, imported {data.retrievedAt}. Sprite IDs are not contiguous; missing IDs were not invented.
      This reference is for GTA V / FiveM, not RedM.</p>
  </section>;
}
